/**
 * Custom React hook for fetching data using Axios and managing API call states.
 *
 * @param {string} url - The URL endpoint for the API request.
 * @param {Object} [options={}] - Configuration options for the API request.
 * @param {string} [options.method="get"] - HTTP method for the request (e.g., "get", "post").
 * @param {Object} [options.params={}] - Query parameters for the request.
 * @param {Object} [options.data={}] - Request body data for non-GET requests.
 * @param {Object} [options.config={}] - Additional Axios configuration options.
 * @param {Function} [options.onSuccess] - Callback function to execute on a successful response.
 * @param {Function} [options.onError] - Callback function to execute on an error response.
 * @param {boolean} [options.enabled=true] - Whether the hook should automatically fetch data.
 * @param {boolean} [options.manual=false] - Whether the hook should fetch data manually.
 *
 * @returns {Object} - An object containing the following properties:
 *   @property {any} apiData - The data returned from the API.
 *   @property {boolean} loading - Indicates whether the API request is in progress.
 *   @property {Error|null} error - The error object if the API request fails, otherwise null.
 *   @property {Function} refetch - Function to manually refetch the data.
 *   @property {Function} mutate - Function to manually mutate the data with custom options.
 */
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { API } from "../Services/api-services";
import Axios from "axios";

function useFetch(url, options = {}) {
  const {
    method = "get",
    params = {},
    data = {},
    config = {},
    onSuccess,
    onError,
    enabled = true,
    manual = false,
  } = options;

  const [apiData, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Monotonic id of the newest request. Only the newest may write state: a
  // superseded request still runs its `finally` AFTER its replacement started,
  // which used to clear the replacement's `loading` and leave stale `apiData`
  // reading as a settled answer for the newer inputs.
  const requestSeqRef = useRef(0);

  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  const memoizedParams = useMemo(() => params, [JSON.stringify(params)]);
  const memoizedData = useMemo(() => data, [JSON.stringify(data)]);
  const memoizedConfig = useMemo(() => config, [JSON.stringify(config)]);

  const fetchData = useCallback(
    async (cancelToken, overrideOptions = {}) => {
      const finalUrl = overrideOptions.url || url;
      const finalMethod = overrideOptions.method || method;
      const finalParams = overrideOptions.params || memoizedParams;
      const finalData = overrideOptions.data || memoizedData;
      const finalConfig = overrideOptions.config
        ? { ...memoizedConfig, ...overrideOptions.config }
        : memoizedConfig;

      // Use override callbacks if provided, otherwise use refs
      const finalOnSuccess = overrideOptions.onSuccess || onSuccessRef.current;
      const finalOnError = overrideOptions.onError || onErrorRef.current;

      if (!finalUrl) return;

      requestSeqRef.current += 1;
      const requestId = requestSeqRef.current;
      const isCurrent = () => requestId === requestSeqRef.current;

      setLoading(true);
      setError(null);

      try {
        let response;

        if (finalMethod.toLowerCase() === "get") {
          response = await API[finalMethod](finalUrl, finalParams, finalData, {
            cancelToken,
            ...finalConfig,
          });
        } else {
          response = await API[finalMethod](finalUrl, finalData, finalParams, {
            cancelToken,
            ...finalConfig,
          });
        }

        if (!Axios.isCancel(response)) {
          // Callbacks are guarded like the state writes: a superseded request's
          // onSuccess delivering stale data to a consumer that stores it is the
          // same out-of-order bug, one layer up.
          if (isCurrent()) {
            setData(response.data);
            finalOnSuccess?.(response.data);
          }
          return response.data;
        }
      } catch (err) {
        if (!Axios.isCancel(err)) {
          if (isCurrent()) {
            setError(err);
            setData(null);
            finalOnError?.(err);
          }
          throw err;
        }
      } finally {
        if (isCurrent()) setLoading(false);
      }
    },
    [url, method, memoizedParams, memoizedData, memoizedConfig],
  );

  useEffect(() => {
    if (!enabled || manual) {
      setLoading(false);
      return;
    }

    const CancelToken = Axios.CancelToken;
    const source = CancelToken.source();

    // fetchData re-throws so that `mutate` call sites can catch — but nothing
    // catches it on this automatic path, and an unhandled rejection trips CRA's
    // dev error overlay, which covers the page and makes a failed background
    // fetch look like the screen crashed. The failure is still recorded in
    // `error` and still reaches `onError`; only the stray rejection is dropped.
    fetchData(source.token).catch(() => {});

    return () => {
      source.cancel("Request canceled");
    };
  }, [fetchData, enabled, manual]);

  const refetch = useCallback(() => {
    if (!enabled || !url) return Promise.resolve();

    const CancelToken = Axios.CancelToken;
    const source = CancelToken.source();

    return fetchData(source.token);
  }, [fetchData, enabled, url]);

  const mutate = useCallback(
    async (mutateOptions = {}) => {
      const CancelToken = Axios.CancelToken;
      const source = CancelToken.source();

      try {
        const result = await fetchData(source.token, mutateOptions);
        return result;
      } catch (error) {
        throw error;
      }
    },
    [fetchData],
  );

  return {
    apiData,
    loading,
    error,
    refetch,
    mutate,
  };
}

export default useFetch;
